/* Аватарка бизнеса или проекта.

   Картинка ужимается до 256px прямо в браузере: снимок с телефона весит мегабайты,
   а в списках она показывается кружком в 40 пикселей. */

import React, { useRef, useState } from "react";

import { Ion } from "./icons.jsx";
import { avatarUrl, uploadImage } from "../lib/api.js";
import { haptic } from "../lib/tg.js";

const SIDE = 256;

/** Обрезает по центру в квадрат и сжимает — на выходе JPEG */
function squeeze(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const side = Math.min(img.width, img.height);
      const canvas = document.createElement("canvas");
      canvas.width = SIDE;
      canvas.height = SIDE;
      canvas
        .getContext("2d")
        .drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, SIDE, SIDE);
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Не вышло сжать картинку"))), "image/jpeg", 0.85);
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => reject(new Error("Не получилось прочитать картинку"));
    img.src = URL.createObjectURL(file);
  });
}

export default function AvatarPicker({ value, onChange }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setErr(null);
    try {
      const { url } = await uploadImage(await squeeze(file));
      haptic.success();
      onChange(url);
    } catch (e2) {
      haptic.error();
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8">
      <div className="flex justify-center">
        <button
          type="button"
          onClick={() => input.current?.click()}
          className="w-24 h-24 rounded-full overflow-hidden flex items-center justify-center active:opacity-70"
          style={{ background: "var(--tg-section-bg)", opacity: busy ? 0.5 : 1 }}
          aria-label={value ? "Заменить фото" : "Загрузить фото"}
        >
          {value ? (
            <img src={avatarUrl(value)} alt="" className="w-full h-full object-cover" />
          ) : (
            <Ion.image size={30} style={{ color: "var(--tg-hint)" }} />
          )}
        </button>
        <input ref={input} type="file" accept="image/*" className="hidden" onChange={pick} />
      </div>
      {err ? (
        <div className="px-8 pt-2 text-center text-[13px]" style={{ color: "var(--fin-red)" }}>{err}</div>
      ) : null}
    </div>
  );
}

/** Кружок с фото, а без фото — первая буква названия */
export function Avatar({ src, name, size = 40, onDark = false }) {
  return (
    <span
      className="inline-flex items-center justify-center rounded-full overflow-hidden shrink-0 font-semibold"
      style={{
        width: size,
        height: size,
        background: onDark ? "rgba(255,255,255,0.16)" : "var(--tg-secondary-bg)",
        color: onDark ? "rgba(255,255,255,0.75)" : "var(--tg-hint)",
        fontSize: size * 0.4,
      }}
    >
      {src ? (
        <img src={avatarUrl(src)} alt="" className="w-full h-full object-cover" />
      ) : (
        (name || "?").trim().charAt(0).toUpperCase()
      )}
    </span>
  );
}
